import { Injectable } from '@angular/core';
import {environment} from "../../environments/environment";

import * as _ from 'lodash';
import { CustomHttpClientService } from './custom-http-client.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class UserService {

  environmentUrl = environment.apiUrl;

  constructor(private http: CustomHttpClientService) {}

  /**
   * REST Handler to fetch all users by buildings for a building manager
   * @param buildingIds
   * @returns {Observable<any>}
   */

  getAllUsersByBuildingIds(buildingIds: string | string[]): Observable<any> {
    // Convert to array if it's a comma-separated string
    const buildingIDs = Array.isArray(buildingIds) ? buildingIds : buildingIds.split(',');
  
    // Make the POST request and handle the response
    return this.http.post<{ /* Specify the expected response type here */ }>(`${this.environmentUrl}buildings/users`, { buildings: buildingIDs })
      .pipe(
        map(response => response /* or perform any transformation you need */)
      );
  }

  /**
   * REST Handler to check if email / user exists in the DB
   * @param email
   * @returns {Observable<any>}
   */
  // isEmailExists(email) {
  //   return this.http.get(this.environmentUrl + 'user/exists/' +email).map(res => res.json());
  // }

  isEmailExists(email: string): Observable<boolean> {
    // Construct the URL with the email parameter
    const url = `${this.environmentUrl}user/exists/${email}`;
  
    // Make the GET request and handle the response
    return this.http.get<{ exists: boolean }>(url).pipe(
      map(response => response.exists) // Assuming the response contains a property 'exists'
    );
  }

  /**
   * REST Handler to get all users in a single building fetched by building ID
   * @param buildingId
   * @returns {Observable<any>}
   */

  getAllUsersByBuildingId(buildingId: string): Observable<any> {
    // Construct the URL with the buildingId parameter
    const url = `${this.environmentUrl}buildings/${buildingId}/users`;
  
    // Make the GET request and handle the response
    return this.http.get<any>(url); // Assuming the response is of type 'any'
  }

  /**
   * Service handler to check of a building has a building manager
   * @param buildingId
   * @returns {Observable<any>}
   */


  isManagerExists(buildingId: string) {
    return this.http.get(`${this.environmentUrl}building/${buildingId}/manager/exists`)
      .pipe(map(res => res));
  }
  /**
   * REST handler to fetch a particular user's settings
   * @returns {Observable<any>}
   */
  getSettings(): Observable<any> {
    const url = `${this.environmentUrl}users/getSettings`;
    return this.http.get<any>(url); // Specify the response type if known
  }

  /**
   * REST handler to fetch a single user's notification settings
   * @param settings
   * @returns {Observable<any>}
   */
  saveNotificationSettings(settings: any): Observable<any> {
    const url = `${this.environmentUrl}users/saveSettings`;
    return this.http.post<any>(url, settings); // Specify the response type if known
  }

  /**
   * Get user details by userID
   * @param userId
   * @returns {Observable<any>}
   */
  getUserById(userId: string): Observable<any> {
    const url = `${this.environmentUrl}users/user/${userId}`;
    return this.http.get<any>(url);
  }
  /**
   * Fetch all user who firstname or lastname starts with a particular letter
   * @param letters
   * @param buildingIds
   * @returns {Observable<any>}
   */
  getAllUsersFilteredByLetter(letters: string, buildingIds: string | string[]): Observable<any> {
    const buildingIDs = Array.isArray(buildingIds) ? buildingIds : buildingIds.split(',');
    const url = `${this.environmentUrl}users/filter`;
    return this.http.post<any>(url, { letters, buildingIds: buildingIDs });
  }

  /**
   * Fetch all user who firstname or lastname starts with a particular letter
   * @param letters
   * @param buildingIds
   * @returns {Observable<any>}
   */
  getAllTeamMembersByBuildingIds(buildingIds: string | string[]): Observable<any> {
    const buildingIDs = Array.isArray(buildingIds) ? buildingIds : buildingIds.split(',');
    const url = `${this.environmentUrl}users/filterstaff`;
    return this.http.post<any>(url, { buildingIds: buildingIDs });
  }
  /**
   * Rest handler to flag a user by userID
   * @param userId
   * @param currentUserid
   * @returns {Observable<Response>}
   */
  flagUserById(userId: string, currentUserid: string): Observable<any> {
    const url = `${this.environmentUrl}user/${userId}/flag/${currentUserid}`;
    return this.http.put<any>(url, {});
  }
  
  /**
   * Disable a user by UserID
   * @param userId
   * @returns {Observable<any>}
   */

  disableUserById(userId: string): Observable<any> {
    const url = `${this.environmentUrl}user/${userId}/disable/`;
    return this.http.put<any>(url, {});
  }

  /**
   * Destroy user record by ID
   * @param userId
   * @returns {Observable<Response>}
   */
  removeUserById(userId) {
    return this.http.delete(this.environmentUrl + 'user/' + userId);
  }

  /**
   * Update a user's detail by userId
   * @param user
   * @returns {Observable<any>}
   */
  updateUserDetails(user: any): Observable<any> {
    const formData: FormData = new FormData();
    const userData = JSON.stringify(user);
    formData.append('userData', userData);
    const url = `${this.environmentUrl}user/${user.id}`;
    return this.http.put<any>(url, formData);
  }
  /**
   * Check if old password matches during change password
   * @param oldPass
   * @param userId
   * @returns {Observable<any>}
   */

  checkOldPassword(oldPass: string, userId: string): Observable<any> {
    const url = `${this.environmentUrl}user/${userId}/pass/${oldPass}`;
    return this.http.get<any>(url);
  }
  /**
   * REST handler to save a new password
   * @param password
   * @param userId
   * @returns {Observable<any>}
   */

  storeNewPassword(password: string, userId: string): Observable<any> {
    const userData = { password: password, userId: userId };
    const url = `${this.environmentUrl}user/${userId}/password`;
    return this.http.post<any>(url, userData);
  }

  /**
   * Send email invites by emailId
   * @param emailIds
   * @returns {Observable<any>}
   */
  sendEmailInvite(emailIds: string[], feed: any = null, build: any = null): Observable<any> {
    const url = `${this.environmentUrl}users/sendEmailInvites`;
    const body = { emailIds: emailIds, feed: feed, build: build };
    return this.http.post<any>(url, body);
  }
  /**
   * Get user profile information by userID
   * @param id
   * @returns {Observable<any>}
   */
  getUserByFacebookId(id: string): Observable<any> {
    console.log(id);
    const url = `${this.environmentUrl}users/userByFacebook/${id}`;
    return this.http.get<any>(url);
  }
  /**
   * Get user's profile image by userID
   * @param userId
   * @returns {Observable<any>}
   */

  getUserProfileImageByUserId(userId: string): Observable<any> {
    const url = `${this.environmentUrl}user/${userId}/picture`;
    return this.http.get<any>(url);
  }
  /**
   * Stores resend request information for an email
   * @param email
   * @returns {Observable<any>}
   */

  resendInvite(email: string, userId: string): Observable<any> {
    const url = `${this.environmentUrl}user/${email}`;
    const body = { userId: userId };
    return this.http.post<any>(url, body);
  }

  importResidentsList(residentsList: any): Observable<any> {
    const url = `${this.environmentUrl}users/importResidentsList`;
    return this.http.post<any>(url, residentsList);
  }

  importLiveMycoopResidentsList(residentsList: any): Observable<any> {
    const url = `${this.environmentUrl}users/importLiveMycoopResidentsList`;
    return this.http.post<any>(url, residentsList); // Specify the response type if known
  }

  getImageUrl() {
    return this.http.get<{ url: string }>('/user/get-presigned-url');
  }
}
